import { appointmentSlot } from '@ghp/domain'
import { cn } from '@ghp/ui'
import { StickyNote } from 'lucide-react'

import type { CachedInquiry, CachedInternalMeeting } from '../../../offline/data-db'
import { isSameMonth } from '../lib/agenda-calendar'
import { dayLabel, dayNumberLabel } from '../lib/agenda-labels'
import {
  agendaItemsOf,
  itemDay,
  itemKey,
  scheduledAtOf,
  type AgendaItem,
} from '../lib/agenda-layout'
import s from './MonthGrid.module.css'

/**
 * La vue Mois : une case par jour, des pastilles compactes — **trois au maximum**, puis `+N`,
 * comme la maquette validée.
 *
 * ⚠️ Le `+N` n'est pas une troncature muette : il est **ouvrable**, et passe la journée en vue
 * Semaine. Sans ce geste, un jour chargé cacherait des rendez-vous sans qu'aucun chemin d'écran
 * ne les ramène.
 */
export interface MonthGridProps {
  /** Les semaines de la grille, débords des mois voisins compris. */
  weeks: readonly (readonly string[])[]
  /** Le jour d'ancrage : ce qui déborde de son mois se peint en retrait. */
  anchor: string
  inquiries: readonly CachedInquiry[]
  /**
   * Les points internes du mois (#319), comptés **dans** les trois pastilles : un jour pris par un
   * brief n'est pas un jour libre, et le `+N` doit le dire.
   */
  meetings: readonly CachedInternalMeeting[]
  /** Aujourd'hui à Paris : ce qui le précède est passé, et s'estompe (#577). */
  today: string
  /**
   * Les créneaux faits (`isDone`, #577), par `itemKey` — calculés une fois par l'écran, comme
   * l'état de tournée.
   */
  done: ReadonlySet<string>
  onOpen: (inquiry: CachedInquiry) => void
  /** Ouvrir la semaine d'un jour — le geste du `+N` et du numéro de jour. */
  onOpenDay: (day: string) => void
}

/** Ce que la maquette montre avant de compter le reste. */
const VISIBLE_PILLS = 3

const WEEKDAYS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.']

export function MonthGrid({
  weeks,
  anchor,
  inquiries,
  meetings,
  today,
  done,
  onOpen,
  onOpenDay,
}: MonthGridProps) {
  const byDay = new Map<string, AgendaItem[]>()

  for (const item of agendaItemsOf(inquiries, meetings)) {
    const day = itemDay(item)
    if (day === null) continue
    byDay.set(day, [...(byDay.get(day) ?? []), item])
  }

  return (
    <div className={s.month}>
      {WEEKDAYS.map((weekday) => (
        <span key={weekday} className={s.weekday} aria-hidden>
          {weekday}
        </span>
      ))}
      {weeks.flat().map((day) => {
        const scheduled = sortedByTime(byDay.get(day) ?? [])
        const hidden = scheduled.length - VISIBLE_PILLS

        return (
          <section
            key={day}
            className={cn(s.cell, !isSameMonth(day, anchor) && s.outside)}
            data-past={day < today ? '' : undefined}
            aria-label={dayLabel(day)}
          >
            <button
              type="button"
              className={s.number}
              // Le numéro seul ne dit rien hors de sa colonne : le nom accessible porte le jour
              // entier, la case en dépend pour être annoncée.
              aria-label={`Ouvrir la semaine du ${dayLabel(day)}`}
              onClick={() => {
                onOpenDay(day)
              }}
            >
              {dayNumberLabel(day)}
            </button>
            {scheduled.slice(0, VISIBLE_PILLS).map((item) =>
              item.kind === 'appointment' ? (
                <button
                  key={itemKey(item)}
                  type="button"
                  className={s.pill}
                  data-done={done.has(itemKey(item)) ? '' : undefined}
                  onClick={() => {
                    onOpen(item.inquiry)
                  }}
                >
                  {/* L'heure de Paris, ou rien quand la date est illisible — la pastille reste. */}
                  <span className={s.pillTime}>{timeOf(item)}</span>
                  {/* Le repère de la note du bureau (#575), entre l'heure et l'adresse. */}
                  {(item.inquiry.comment ?? '') !== '' && (
                    <StickyNote
                      size={12}
                      className={s.note}
                      role="img"
                      aria-label="Note du bureau"
                    />
                  )}
                  <span className={s.pillLabel}>
                    {item.inquiry.address.street ?? item.inquiry.reference ?? 'Sans référence'}
                  </span>
                </button>
              ) : (
                // Rien à ouvrir, comme sur la vue Semaine (`InternalMeetingCard`) : une pastille
                // de texte, pas un bouton.
                <span
                  key={itemKey(item)}
                  className={cn(s.pill, s.internal)}
                  data-done={done.has(itemKey(item)) ? '' : undefined}
                  aria-label={`Point interne : ${item.meeting.title}`}
                >
                  <span className={s.pillTime}>{timeOf(item)}</span>
                  <span className={s.pillLabel}>{item.meeting.title}</span>
                </span>
              ),
            )}
            {hidden > 0 && (
              <button
                type="button"
                className={s.more}
                onClick={() => {
                  onOpenDay(day)
                }}
              >
                +{hidden} le {dayNumberLabel(day)}
              </button>
            )}
          </section>
        )
      })}
    </div>
  )
}

/**
 * Les créneaux d'une case, dans l'ordre de la journée. Sans ce tri, les trois pastilles visibles
 * seraient les trois premières **du cache** : l'agent lirait un après-midi et manquerait son
 * premier rendez-vous du matin.
 */
function sortedByTime(items: readonly AgendaItem[]): AgendaItem[] {
  // ⚠️ Comparé en **instants**, jamais en chaînes : `scheduledAt` accepte un décalage, et deux
  // écritures équivalentes du même instant ne se trient pas dans l'ordre de l'horloge.
  return [...items].sort((left, right) => instantOf(left) - instantOf(right))
}

/** L'heure de Paris d'un créneau, `undefined` quand sa date est illisible. */
function timeOf(item: AgendaItem): string | undefined {
  return appointmentSlot(scheduledAtOf(item))?.time
}

/** L'instant d'un créneau ; une date illisible passe en fin de journée plutôt que de lever. */
function instantOf(item: AgendaItem): number {
  const at = new Date(scheduledAtOf(item))

  return Number.isNaN(at.getTime()) ? Number.MAX_SAFE_INTEGER : at.getTime()
}
